<!--
SPDX-FileCopyrightText: 2025 Land Baden-Württemberg <InnoLab@stm.bwl.de>

SPDX-License-Identifier: MPL-2.0
-->

<script>
import { onDestroy, onMount } from 'svelte';
import { getApiUrl } from '../../config.js';
import { renderMarkdown } from '../../utils/markdownParser';

import { fly } from 'svelte/transition';
import Alert from '../../components/Alert.svelte';
import DragAndDrop from '../../components/DragAndDrop.svelte';
import FeedbackButtons from '../../components/FeedbackButtons.svelte';
import FileUploadButton from '../../components/FileUploadButton.svelte';
import InputTextarea from '../../components/InputTextarea.svelte';
import SkeletonLoader from '../../components/SkeletonLoader.svelte';
import { get } from '../../utils/CoreService';
import sendFeedback from '../../utils/SendFeedback.js';
import uiStore from '../../utils/UIStore';
import { copyWithDisclaimers } from '../../utils/copyHook.js';
import websocketsStore from '../../utils/websocketsStore';
import fetchSummary from './FetchSummary.js';
import fetchFileSummary from './FetchSummaryFile.js';
import LengthSlider from './LengthSlider.svelte';
import mapSliderValue from './MapSliderValue.js';

const SUMMARY_COPY_DISCLAIMER =
  'Hinweis: Diese Zusammenfassung wurde mit KI-Unterstützung erzeugt und kann Fehler enthalten. Bitte prüfen Sie den Inhalt vor der weiteren Verwendung.';

let warningCloud = '';
let warningHalluzination = '';
let summaryText = '';
let sendButtonTooltip = '';
const sliderDefaultValue = 0.23;
const TOPICS_CHAR_LIMIT = 100;
let sliderValue = sliderDefaultValue;
let lastMappedSliderValue = mapSliderValue(sliderValue);
let topicInput = '';
let remainingChars = TOPICS_CHAR_LIMIT;
let topicsLabel = '';
let topicsPlaceholder = '';
let thematicFocusLabel = '';
let usedTopics = ''; // Store topics used for the current summary
let hasShownTopicsLimitWarning = false;
let summaryNoContentWarning = '';
let uploadedFile = null;
let uploadedText = null; //from clipboard
let summaryResult = null;
let parsedText = null;
let currentRating = 0;
let llm = null;
let isDropzoneActive = false;
let editText = false;
let resetTextArea = () => {};
let isLoading = false;

// Character limit for topics input
$: remainingChars = Math.max(0, TOPICS_CHAR_LIMIT - (topicInput ? topicInput.length : 0));
$: if (topicInput && topicInput.length > TOPICS_CHAR_LIMIT) {
  topicInput = topicInput.slice(0, TOPICS_CHAR_LIMIT);
  if (!hasShownTopicsLimitWarning) {
    websocketsStore.currentMessage.warning.set({
      message: `Die maximale Eingabelänge von ${TOPICS_CHAR_LIMIT} Zeichen wurde erreicht. Bitte kürzen Sie Ihre Eingabe für den thematischen Fokus.`,
      type: 'warning',
    });
    hasShownTopicsLimitWarning = true;
  }
} else if (topicInput && topicInput.length < TOPICS_CHAR_LIMIT) {
  hasShownTopicsLimitWarning = false;
}

onMount(() => {
  document.addEventListener('keydown', handleKeydown);

  //load ui texts from ui Store
  uiStore.textStore.subscribe((store) => {
    if (store.texts != null) {
      let data = store.texts;
      warningCloud = data.warnhinweisCloud;
      summaryText = renderMarkdown(data.summaryText);
      warningHalluzination = renderMarkdown(data.warnhinweisRecherche);
      topicsLabel = data.summaryTopicsLabel;
      topicsPlaceholder = data.summaryTopicsPlaceholder;
      thematicFocusLabel = data.summaryThematicFocusLabel;
      summaryNoContentWarning = data.summaryNoContentWarning;
    }
  });

  //load summary state from store
  uiStore.summaryStore.subscribe((store) => {
    summaryResult = store.result;
    currentRating = store.rating;
    uploadedFile = store.file;
    uploadedText = store.text;
  });

  //get llm info from backend
  const url = `${getApiUrl()}/summary/llms`;
  try {
    const response = get(url)
      .then((res) => {
        console.log(res);
        return res.json();
      })
      .then((json) => {
        console.log(json);
        llm = json[0];
      });
  } catch (error) {
    console.log(error);
  }
});

onDestroy(() => {
  document.removeEventListener('keydown', handleKeydown);

  uiStore.summaryStore.update(() => ({
    result: summaryResult,
    rating: currentRating,
    file: uploadedFile,
    text: uploadedText,
  }));
  resetUI();
});

let features;
uiStore.featureStore.subscribe((fs) => {
  features = fs;
});

let uiTexts;
uiStore.textStore.subscribe((store) => {
  uiTexts = store.texts;
});

function resetUI() {
  uploadedFile = null;
  uploadedText = null;
  summaryResult = null;
  parsedText = null;
  currentRating = 0;
  editText = false;
  sliderValue = sliderDefaultValue;
  topicInput = '';
  usedTopics = '';
  resetTextArea();
  websocketsStore.websocket.resetMessages();
}

async function sendRequest() {
  if (!llm) return;

  // Check if no content is provided
  if (uploadedFile === null && uploadedText === null) {
    websocketsStore.currentMessage.warning.set({
      message:
        summaryNoContentWarning ||
        'Bitte fügen Sie ein Dokument oder einen Text aus der Zwischenablage ein, bevor Sie die Zusammenfassung anfordern.',
      type: 'warning',
    });
    return;
  }

  websocketsStore.websocket.resetMessages();
  summaryResult = null;
  parsedText = null;
  isLoading = true;
  editText = false;
  let mappedSliderValue = mapSliderValue(sliderValue);
  lastMappedSliderValue = mappedSliderValue;
  let api_result = null;
  const topics = topicInput.trim();

  try {
    // if file upload
    if (uploadedFile != null) {
      const formData = new FormData();
      formData.append('file', uploadedFile.content);
      formData.append('output_length', mappedSliderValue);
      formData.append('language_model', llm.name);

      api_result = await fetchFileSummary(formData, topics);

      // text from clipboard
    } else if (uploadedText != null) {
      api_result = await fetchSummary(uploadedText, mappedSliderValue, llm.name, topics);
    }

    if (api_result.status !== 200) {
      const res = await api_result.json();
      throw new Error(res.detail);
    }
  } catch (err) {
    isLoading = false;
    websocketsStore.currentMessage.error.update(() => ({
      type: 'ERROR',
      message: err.message,
    }));
    return;
  }

  const res = await api_result.json();

  if (res.warning_msg !== '') {
    websocketsStore.currentMessage.warning.update(() => ({
      type: 'WARNING',
      message: res.warning_msg,
    }));
  }
  summaryResult = res.summary;
  parsedText = res.parsed_text;
  usedTopics = topics; // Store the topics used for this summary

  isLoading = false;
}

function addFiles(fileList) {
  uploadedFile = {
    id: window.crypto.randomUUID(),
    type: 'file',
    origin: 'upload',
    label: fileList[0].name,
    content: fileList[0],
  };
}

//Paste from clipboard using shortcut
function handleKeydown(event) {
  if (event.ctrlKey && event.key === 'v' && uploadedText == null) {
    addTextFromClipboard();
  }
}

function addTextFromClipboard() {
  /*
        Paste text from clipboard
         */
  navigator.clipboard
    .readText()
    .then((text) => {
      if (text.length === 0) {
        websocketsStore.currentMessage.info.update(() => ({
          type: 'INFO',
          message: uiTexts.rag.clipboardEmpty,
        }));
        throw Error(uiTexts.rag.clipboardEmpty);
      }
      uploadedText = text;
      return text;
    })
    .catch((reason) => console.debug(uiTexts.rag.genericError + ` ${reason}`));
}

async function giveFeedback(rating, feedbackText) {
  currentRating = rating;

  let type = 'text';

  if (uploadedFile != null) {
    console.log(uploadedFile);
    let splittedName = uploadedFile.content.name.split('.');
    type = splittedName[splittedName.length - 1];
  }

  const body = {
    feedback: rating,
    feedback_text: feedbackText,
    llm: {
      name: llm.name,
      label: llm.label,
      is_remote: llm.is_remote,
    },
    source_type: type,
    text_length: parsedText.length,
    desired_summary_length: lastMappedSliderValue * 4000, //number of pages * characters per page
    summary_length: summaryResult.length,
  };

  sendFeedback(body, `${getApiUrl()}/feedback/summary`);
}

function handleEnterKey(event) {
  if (uploadedText != null) {
    // newline on [SHIFT+Enter]
    if (event.key === 'Enter' && event.shiftKey) {
      uploadedText = `${uploadedText}\n`;
      event.preventDefault();
    }
  }
}
</script>

<svelte:head>
  <title>{uiTexts.pageTitles.summary}</title>
</svelte:head>

<main style="padding-top: 90px">
  {#if !llm || llm.is_remote}
    <div class="top-info-bar">
      <Alert tone="cloud">
        <span class="red">{uiTexts.warning.attention}</span>
        <span style="color:#000">{warningCloud} </span>
      </Alert>
    </div>
  {/if}
  <div in:fly={{ y: -20, duration: 500, delay: 200 }} class="page-container">
    <div class="hinweis">
      <div style="flex:1;padding-right:24px">
        <p style="padding-top:0" class="textlink">
          {@html summaryText}
        </p>
      </div>

      <div class="reset">
        <button class="secondary delete" on:click={resetUI}
          >{uiTexts.summaryPage.reset}
        </button>
      </div>
    </div>

    {#if uploadedFile == null && uploadedText == null}
      <div
        class="content-container"
        style="display:flex; flex-direction:row; gap:20px;     align-items: center;"
      >
        <!-- File Upload-->
        <div style="max-width:30%; flex: 1">
          <FileUploadButton addFiles={(files) => addFiles(files)} />
        </div>

        <span>{uiTexts.summaryPage.or}</span>
        <!-- From clipboard-->
        <div class="zwischenablage-input" style="flex:1;max-width:30%">
          <button
            class="secondary"
            on:click={addTextFromClipboard}
            style="width:100%;text-align:left;justify-content:left; height:100%"
          >
            {uiTexts.summaryPage.pasteFromClipboard}
          </button>
        </div>
      </div>
    {/if}

    {#if uploadedFile != null}
      <div class="content-container">
        <div class="source-item" style="width:fit-content; margin:0px;">
          <div class="label">{uploadedFile.label}</div>
          <button
            aria-label={uiTexts.summaryPage.removeFileAria}
            title={uiTexts.summaryPage.removeFileTitle}
            class="delete-source"
            style="background-color:transparent;"
            on:click={() => {
              uploadedFile = null;
            }}
          ></button>
        </div>
      </div>
    {/if}

    {#if uploadedText != null}
      <div class="content-container">
        <div
          class="source-item"
          style="border: {editText
            ? '2px solid black'
            : '1px solid black'}; width:fit-content;margin:0px;"
        >
          <div class="label">{uploadedText.substring(0, 100)}...</div>
          {#if !editText}
            <button
              aria-label={uiTexts.summaryPage.editFileAria}
              title={uiTexts.summaryPage.editFileTitle}
              class="open-source source-button"
              style="background-color:transparent;"
              on:click={() => {
                editText = true;
              }}
            ></button>
          {/if}
          {#if editText}
            <button
              aria-label={uiTexts.summaryPage.closeEditAria}
              title={uiTexts.summaryPage.closeEditTitle}
              class="close-source source-button"
              style="background-color:transparent;"
              on:click={() => {
                editText = false;
              }}
            ></button>
          {/if}
          <div style="border-left: 1px solid black">
            <button
              aria-label={uiTexts.summaryPage.removeTextAria}
              title={uiTexts.summaryPage.removeTextTitle}
              class="delete-source"
              style="background-color:transparent; margin-left:4px;"
              on:click={() => {
                editText = false;
                uploadedText = null;
              }}
            ></button>
          </div>
        </div>
        {#if editText}
          <div style="border: 1px solid black; margin-top:-1px">
            <InputTextarea
              resizable="True"
              bind:newMessage={uploadedText}
              {handleEnterKey}
              bind:reset={resetTextArea}
              name={uiTexts.summaryPage.clipboardInputName}
              placeholder=""
              style="border: 1px solid #000; resize:none;    margin-top: -1px;"
              showFileUpload={false}
            ></InputTextarea>
          </div>
        {/if}
      </div>
    {/if}

    <div class="content-container controls-wrapper">
      <div class="control-row">
        <div class="control-box topics-field">
          <label class="topics-label" for="topicsInput">{topicsLabel}</label>
          <div class="topics-input">
            <input
              id="topicsInput"
              type="text"
              placeholder={topicsPlaceholder}
              bind:value={topicInput}
            />
          </div>
          <div class="char-counter">
            Zeichenlimit verbleibend: {remainingChars} / {TOPICS_CHAR_LIMIT}
          </div>
        </div>
        <div class="control-box slider-block">
          <div class="slider-row">
            <LengthSlider bind:sliderValue />
            <div class="send-button-wrapper">
              <button
                class="send-button"
                aria-label="Anfrage senden"
                disabled={isLoading || llm === null}
                on:click={() => sendRequest()}
                title={sendButtonTooltip}
              >
              </button>
            </div>
          </div>
          <div class="halluzination-warning">
            {@html warningHalluzination}
          </div>
        </div>
      </div>
    </div>

    {#if summaryResult != null || isLoading}
      <div style="padding-top: 10px;">
        <!-- Antworten -->
        <div class="frage">
          <span class="flex1"> {uiTexts.summaryPage.summaryHeader} </span>

          <div style="display:flex;">
            {#if llm != null}
              <span style="font-size: small; color: #999999">
                {llm.label}
              </span>
            {/if}

            <button
              title={uiTexts.summaryPage.deleteSummaryTitle}
              class="delete-question source-button"
              aria-label={uiTexts.summaryPage.deleteSummaryAria}
              style="background-color:transparent;"
              on:click={() => (summaryResult = null)}
            ></button>
          </div>
        </div>

        <div
          class="answers"
          use:copyWithDisclaimers={{
            before: SUMMARY_COPY_DISCLAIMER,
            after: SUMMARY_COPY_DISCLAIMER,
            selectors: ['.summary-copy-text'],
          }}
        >
          {#if uploadedFile != null}
            <span class="answer-source-type">
              {uiTexts.summaryPage.originalFile} {uploadedFile.label}
            </span>
          {/if}

          {#if uploadedText != null}
            <span class="answer-source-type">
              {uiTexts.summaryPage.originalText} {uploadedText}
            </span>
          {/if}

          {#if usedTopics && usedTopics.trim() !== ""}
            <span class="answer-source-type">
              {thematicFocusLabel}: {usedTopics}
            </span>
          {/if}


          {#if isLoading}
            <SkeletonLoader />
          {/if}

          {#if summaryResult != null}
            <div class="summary-copy-text" style="padding-top:12px;">
              {@html renderMarkdown(summaryResult)}
            </div>
            <div class="answer-actions">
              {#if features.feedback}
                <FeedbackButtons
                  messageContent={summaryResult}
                  buttonClicked={currentRating}
                  copyDisclaimerBefore={SUMMARY_COPY_DISCLAIMER}
                  copyDisclaimerAfter={SUMMARY_COPY_DISCLAIMER}
                  callback={(rating, feedbackText) =>
                    giveFeedback(rating, feedbackText)}
                />
              {/if}
            </div>
          {/if}
        </div>
      </div>
    {/if}
  </div>

  <DragAndDrop {isDropzoneActive} addFiles={(files) => addFiles(files)} />
</main>

<style>
  main {
    text-align: center;
    padding-top: 100px;
    padding-bottom: 0px;
    max-width: none;
    height: 100vh;
    justify-content: center;
  }

  .content-container {
    display: flex;
    flex-direction: column;
    background-color: var(--bg-color);
    padding: 10px;
    text-align: left;
  }
    .controls-wrapper {
    background-color: transparent;
    padding: 0;
  }
  .control-row {
    display: flex;
    gap: 0;
    align-items: stretch;
    flex-wrap: wrap;
  }
  .control-box {
    flex: 1 1 320px;
    min-width: 280px;
    padding: 12px 14px;
    background-color: var(--bg-color);
  }
  .control-box + .control-box {
    border-left: 4px solid #fff;
  }
  .slider-block {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .slider-row {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
        gap: 20px;
    flex-wrap: wrap;
  }
  .halluzination-warning {
    margin-top: 8px;
  }
  .topics-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .topics-label {
    font-weight: 600;
    font-size: 14px;
  }
  .topics-input {
    border: 1px solid #c9c9c9;
    background-color: #fff;
    padding: 8px 10px;
  }
  .char-counter {
    margin-top: 4px;
    text-align: right;
    font-size: 12px;
    color: #555;
  }
  .topics-input input {
    width: 100%;
    border: none;
    outline: none;
    font-size: 14px;
    background: transparent;
  }
  .send-button-wrapper {
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }
</style>
